// Visitor journey tracker — storefront-side.
//
// Drop into the Shopify theme (e.g. as `assets/journey-tracker.js` and
// include with `{{ 'journey-tracker.js' | asset_url | script_tag }}` in
// `layout/theme.liquid`, just before </body>).
//
// Posts batched events to the Fly app at INGEST_URL. Sends per-event
// timestamps captured at the moment the event happened (not flush time),
// emits a 15s heartbeat while the tab is visible + user is active, and
// closes out with a `session_end` beacon on pagehide / visibility hidden.
//
// No PII captured. Anonymous sid stored in a first-party cookie.

(function () {
  "use strict";

  // ── Bot guard ──────────────────────────────────────────────────────────
  // Crawlers (especially Googlebot's evergreen-Chromium renderer) execute
  // page JS for SEO indexing. Without a guard their renders show up as
  // real visitor sessions with no referrer/UTM and get attributed to
  // "Direct" traffic, polluting funnel + CVR analytics.
  //
  // Bail out before anything else runs: no cookie, no queue, no flush.
  // Conservative regex — only well-known crawler / preview / audit UA
  // substrings, no bare "bot" catch-all (which false-positives real browsers).
  var BOT_UA_RE =
    /Googlebot|AdsBot-Google|Mediapartners-Google|FeedFetcher-Google|Google-InspectionTool|GoogleOther|Storebot-Google|Chrome-Lighthouse|HeadlessChrome|bingbot|YandexBot|Baiduspider|DuckDuckBot|facebookexternalhit|Twitterbot|LinkedInBot|WhatsApp|TelegramBot|Applebot|Pinterest|Slurp|Sogou|Discordbot|Bytespider|PetalBot|SemrushBot|AhrefsBot|MJ12bot|DotBot/i;
  if (BOT_UA_RE.test(navigator.userAgent || "")) {
    // Expose a marker so debugging / verification can confirm the skip path
    // without inspecting closure state. Safe to leave on the global.
    try { window.__dluxJourneyBotSkip = true; } catch (_) {}
    return;
  }

  // ── Config ─────────────────────────────────────────────────────────────
  var INGEST_URL = "https://dlux-essentials-review-app.fly.dev/api/journey/ingest";
  var SID_COOKIE = "dlux_jsid";
  var SID_TTL_DAYS = 30;
  var FLUSH_INTERVAL_MS = 5000;
  var HEARTBEAT_INTERVAL_MS = 15000;
  var FIRST_HEARTBEAT_MS = 3000;
  var IDLE_TIMEOUT_MS = 60000;
  var MAX_BATCH = 40;

  // ── State ──────────────────────────────────────────────────────────────
  var sid = readSidCookie() || cryptoUUID();
  writeSidCookie(sid);

  var queue = [];
  var lastActivityAt = Date.now();
  var pageEnteredAt = Date.now();
  var dwellAccumMs = 0;
  var dwellLastTick = Date.now();
  var sentMeta = false;
  var flushTimer = null;
  var heartbeatTimer = null;

  // ── Helpers ────────────────────────────────────────────────────────────
  function cryptoUUID() {
    if (window.crypto && typeof window.crypto.randomUUID === "function") {
      return window.crypto.randomUUID();
    }
    // RFC4122 v4 fallback
    var b = new Uint8Array(16);
    (window.crypto || window.msCrypto).getRandomValues(b);
    b[6] = (b[6] & 0x0f) | 0x40;
    b[8] = (b[8] & 0x3f) | 0x80;
    var hex = [];
    for (var i = 0; i < 16; i++) hex.push(b[i].toString(16).padStart(2, "0"));
    return (
      hex.slice(0, 4).join("") + "-" +
      hex.slice(4, 6).join("") + "-" +
      hex.slice(6, 8).join("") + "-" +
      hex.slice(8, 10).join("") + "-" +
      hex.slice(10, 16).join("")
    );
  }

  function readSidCookie() {
    var m = document.cookie.match(/(?:^|; )dlux_jsid=([0-9a-f-]{36})/i);
    return m ? m[1] : null;
  }

  function writeSidCookie(value) {
    var d = new Date();
    d.setTime(d.getTime() + SID_TTL_DAYS * 24 * 60 * 60 * 1000);
    document.cookie =
      SID_COOKIE +
      "=" +
      value +
      "; expires=" +
      d.toUTCString() +
      "; path=/; SameSite=Lax; Secure";
  }

  function detectDevice() {
    var ua = navigator.userAgent || "";
    if (/iPad|Tablet/i.test(ua)) return "tablet";
    if (/Mobi|Android|iPhone/i.test(ua)) return "mobile";
    return "desktop";
  }

  function readUtm() {
    var params = new URLSearchParams(window.location.search);
    var keys = ["source", "medium", "campaign", "term", "content"];
    var out = {};
    var found = false;
    keys.forEach(function (k) {
      var v = params.get("utm_" + k);
      if (v) {
        out[k] = v.slice(0, 256);
        found = true;
      }
    });
    return found ? out : undefined;
  }

  var EMAIL_STORAGE_KEY = "dlux_jemail";
  var EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
  var pendingEmail = readStoredEmail();

  function readStoredEmail() {
    try {
      var v = window.localStorage.getItem(EMAIL_STORAGE_KEY);
      if (v && EMAIL_RE.test(v)) return v;
    } catch (_) {}
    return null;
  }
  function writeStoredEmail(v) {
    try {
      window.localStorage.setItem(EMAIL_STORAGE_KEY, v);
    } catch (_) {}
  }
  function maskEmail(v) {
    var at = v.indexOf("@");
    if (at <= 0) return "***";
    return v[0] + "***@" + v.slice(at + 1);
  }

  function readCustomerId() {
    // Shopify exposes the logged-in customer id via window.__st or
    // ShopifyAnalytics on most themes. Fall back to null.
    try {
      if (
        window.ShopifyAnalytics &&
        window.ShopifyAnalytics.meta &&
        window.ShopifyAnalytics.meta.page &&
        window.ShopifyAnalytics.meta.page.customerId
      ) {
        return String(window.ShopifyAnalytics.meta.page.customerId);
      }
    } catch (_) {}
    return null;
  }

  function buildMeta() {
    var meta = {};
    if (!sentMeta) {
      sentMeta = true;
      meta.ua = (navigator.userAgent || "").slice(0, 1024);
      meta.ref = (document.referrer || "").slice(0, 2048) || undefined;
      meta.device = detectDevice();
      meta.landing_path = window.location.pathname;
      var customerId = readCustomerId();
      if (customerId) meta.customer_id = customerId;
      var utm = readUtm();
      if (utm) meta.utm = utm;
    }
    // Email rides on every flush until the server acknowledges by setting
    // emailHash on the session row. We don't have an explicit ACK protocol;
    // first-write-wins on the server makes resending harmless.
    if (pendingEmail) {
      meta.email = pendingEmail;
    }
    return Object.keys(meta).length > 0 ? meta : undefined;
  }

  function push(type, data, path) {
    if (queue.length >= MAX_BATCH) flush();
    var ev = { type: type, t: Date.now() };
    if (path) ev.path = path;
    else if (window.location && window.location.pathname) ev.path = window.location.pathname;
    if (data && typeof data === "object") ev.data = data;
    queue.push(ev);
    scheduleFlush();
  }

  function scheduleFlush() {
    if (flushTimer) return;
    flushTimer = setTimeout(flush, FLUSH_INTERVAL_MS);
  }

  function flush(useBeacon) {
    if (flushTimer) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
    if (queue.length === 0) return;

    var payload = {
      sid: sid,
      events: queue.splice(0, queue.length),
      meta: buildMeta(),
    };

    var body = JSON.stringify(payload);
    if (useBeacon && navigator.sendBeacon) {
      try {
        var blob = new Blob([body], { type: "application/json" });
        navigator.sendBeacon(INGEST_URL, blob);
        return;
      } catch (_) {}
    }
    // Fetch with keepalive so a fire-and-forget post survives nav.
    try {
      fetch(INGEST_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: body,
        keepalive: true,
        credentials: "omit",
      }).catch(function () {});
    } catch (_) {}
  }

  // ── Heartbeat + idle detection ─────────────────────────────────────────
  function isVisible() {
    return document.visibilityState !== "hidden";
  }
  function isActive() {
    return Date.now() - lastActivityAt < IDLE_TIMEOUT_MS;
  }

  function tickHeartbeat() {
    var now = Date.now();
    if (isVisible() && isActive()) {
      dwellAccumMs += now - dwellLastTick;
    }
    dwellLastTick = now;

    if (isVisible() && isActive()) {
      push("heartbeat", { dwell_ms: dwellAccumMs });
    }
  }

  function startHeartbeat() {
    if (heartbeatTimer) return;
    dwellLastTick = Date.now();
    // Fire an early heartbeat to catch short visits (<15s) so duration
    // doesn't collapse to 0. After the first one, settle into the regular
    // interval.
    setTimeout(function () {
      tickHeartbeat();
      heartbeatTimer = setInterval(tickHeartbeat, HEARTBEAT_INTERVAL_MS);
    }, FIRST_HEARTBEAT_MS);
  }

  function markActivity() {
    lastActivityAt = Date.now();
  }

  ["mousemove", "scroll", "keydown", "touchstart", "click"].forEach(function (ev) {
    window.addEventListener(ev, markActivity, { passive: true, capture: true });
  });

  // ── Lifecycle: initial + end ───────────────────────────────────────────
  var firedScrollMilestones = Object.create(null);
  function resetScrollMilestones() {
    firedScrollMilestones = Object.create(null);
  }
  function emitPageView() {
    resetScrollMilestones();
    push("page_view", undefined, window.location.pathname);
  }

  function endSession() {
    push("session_end", { dwell_ms: dwellAccumMs });
    flush(true);
  }

  document.addEventListener("visibilitychange", function () {
    if (document.visibilityState === "hidden") {
      // Accumulate trailing visible-active time before flushing.
      var now = Date.now();
      if (isActive()) dwellAccumMs += now - dwellLastTick;
      dwellLastTick = now;
      endSession();
    } else {
      dwellLastTick = Date.now();
    }
  });

  window.addEventListener("pagehide", endSession);

  // ── Domain-specific events ─────────────────────────────────────────────
  // Product / collection / cart detection from the URL.
  function emitContextEvent() {
    var p = window.location.pathname;
    if (/^\/products\//.test(p)) {
      var handleMatch = p.match(/^\/products\/([^/?#]+)/);
      push("product_view", handleMatch ? { handle: handleMatch[1] } : undefined);
    } else if (/^\/collections\//.test(p)) {
      var colMatch = p.match(/^\/collections\/([^/?#]+)/);
      push("collection_view", colMatch ? { handle: colMatch[1] } : undefined);
    } else if (p === "/search") {
      var q = new URLSearchParams(window.location.search).get("q") || "";
      push("search", { q: q.slice(0, 256) });
    } else if (/^\/checkouts?\//.test(p)) {
      push("begin_checkout", undefined);
    }
  }

  // Dedupe guard for add_to_cart. Both the form-submit fallback and the
  // fetch patch fire on the same click on Dawn-derived themes (capture-phase
  // submit runs before the theme's bubble-phase preventDefault, then the
  // theme posts via fetch). Drop the second hit if it's the same variant
  // within ATC_DEDUPE_MS.
  var ATC_DEDUPE_MS = 1500;
  var lastAtcAt = 0;
  var lastAtcVariant = null;
  function pushAddToCart(data) {
    var variant = data && data.variant_id ? String(data.variant_id) : "";
    var now = Date.now();
    if (
      variant &&
      variant === lastAtcVariant &&
      now - lastAtcAt < ATC_DEDUPE_MS
    ) {
      // Merge any new fields onto the queued event so we don't lose qty when
      // the first emitter didn't have it. queue[last] is the most recently
      // pushed event from the same path; in practice always the original ATC.
      for (var i = queue.length - 1; i >= 0; i--) {
        if (queue[i].type === "add_to_cart") {
          var prev = queue[i].data || {};
          var merged = Object.assign({}, prev, data);
          queue[i].data = merged;
          break;
        }
      }
      return;
    }
    lastAtcAt = now;
    lastAtcVariant = variant || null;
    push("add_to_cart", data);
    stampSidOnCart();
  }

  // Stamp our sid onto cart.attributes so it survives the cross-domain
  // jump into checkout. Shopify auto-promotes cart attributes onto the
  // order's note_attributes, which the orders/create webhook reads to
  // attribute the purchase back to this journey session.
  // Fires at most once per session (sessionStorage flag).
  var SID_STAMP_KEY = "dlux_sid_stamped";
  function stampSidOnCart() {
    try {
      if (window.sessionStorage.getItem(SID_STAMP_KEY)) return;
    } catch (_) {}
    if (!sid) return;
    try {
      fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          attributes: { dlux_session_id: sid },
        }),
        keepalive: true,
      })
        .then(function () {
          try { window.sessionStorage.setItem(SID_STAMP_KEY, "1"); } catch (_) {}
        })
        .catch(function () {});
    } catch (_) {}
  }

  // Hook Shopify cart add (works on themes that POST to /cart/add.js via fetch).
  (function patchCartAdd() {
    if (!window.fetch) return;
    var origFetch = window.fetch;
    window.fetch = function (input, init) {
      try {
        var url = typeof input === "string" ? input : input && input.url;
        if (url && /\/cart\/add(\.js)?/.test(url)) {
          var data = parseAtcInit(init) || {};
          // Fallback: derive handle from current page if PDP.
          if (!data.product_handle) {
            var m = window.location.pathname.match(/^\/products\/([^/?#]+)/);
            if (m) data.product_handle = m[1];
          }
          pushAddToCart(Object.keys(data).length ? data : {});
        }
      } catch (_) {}
      return origFetch.apply(this, arguments);
    };
  })();

  function parseAtcInit(init) {
    if (!init || !init.body) return null;
    var body = init.body;
    var data = {};
    try {
      if (typeof body === "string") {
        // x-www-form-urlencoded or JSON string
        if (body[0] === "{") {
          var json = JSON.parse(body);
          if (json && json.id) data.variant_id = String(json.id);
          if (json && json.quantity) data.quantity = Number(json.quantity);
          if (json && json.items && json.items[0]) {
            data.variant_id = String(json.items[0].id);
            data.quantity = Number(json.items[0].quantity);
          }
        } else {
          var p = new URLSearchParams(body);
          if (p.get("id")) data.variant_id = String(p.get("id"));
          if (p.get("quantity")) data.quantity = Number(p.get("quantity"));
        }
      } else if (body instanceof FormData) {
        if (body.get("id")) data.variant_id = String(body.get("id"));
        if (body.get("quantity")) data.quantity = Number(body.get("quantity"));
      }
    } catch (_) {}
    return Object.keys(data).length ? data : null;
  }

  // Listen for the standard Shopify "Cart" form submit as a fallback.
  document.addEventListener(
    "submit",
    function (e) {
      var form = e.target;
      if (!form || !form.action || !/\/cart\/add/.test(form.action)) return;
      var data = {};
      var idEl = form.querySelector('[name="id"]');
      if (idEl && idEl.value) data.variant_id = String(idEl.value);
      var qtyEl = form.querySelector('[name="quantity"]');
      if (qtyEl && qtyEl.value) data.quantity = Number(qtyEl.value);
      var handle =
        form.getAttribute("data-product-handle") ||
        (window.location.pathname.match(/^\/products\/([^/?#]+)/) || [])[1];
      if (handle) data.product_handle = handle;
      pushAddToCart(data);
    },
    true,
  );

  // Variant select (PDP) — fires on <select name="id"> change.
  document.addEventListener(
    "change",
    function (e) {
      var t = e.target;
      if (t && t.tagName === "SELECT" && t.name === "id") {
        push("variant_select", { variant_id: String(t.value || "") });
      }
    },
    true,
  );

  // Quantity change (PDP only) — fires on .quantity__input change with the
  // new qty. Cart-drawer line-item qty inputs share this class but emit
  // through the fetch hook below (POST /cart/change.js) to avoid duplicate
  // events. We skip the DOM listener when the input lives inside the cart.
  document.addEventListener(
    "change",
    function (e) {
      var t = e.target;
      if (!t || !t.classList || !t.classList.contains("quantity__input")) return;
      if (
        t.closest &&
        t.closest(
          "cart-drawer, cart-items, cart-drawer-items, #CartDrawer, .cart-items",
        )
      ) {
        return; // cart-side change — fetch hook emits this.
      }
      var qty = parseInt(t.value, 10);
      if (!Number.isFinite(qty)) return;
      push("quantity_change", {
        variant_id: t.getAttribute("data-quantity-variant-id") || undefined,
        quantity: qty,
        source: "pdp",
      });
    },
    true,
  );

  // PDP scroll depth — fire once per milestone (25 / 50 / 75 / 100)
  // while user scrolls down a product page. Milestones reset on page_view.
  (function pdpScroll() {
    if (!/^\/products\//.test(window.location.pathname)) return;
    var MILESTONES = [25, 50, 75, 100];
    var scrollTicking = false;
    function checkMilestones() {
      scrollTicking = false;
      var h = document.documentElement;
      var ratio =
        (h.scrollTop + window.innerHeight) / Math.max(h.scrollHeight, 1);
      var pct = Math.min(100, Math.round(ratio * 100));
      for (var i = 0; i < MILESTONES.length; i++) {
        var m = MILESTONES[i];
        if (pct >= m && !firedScrollMilestones[m]) {
          firedScrollMilestones[m] = true;
          push("pdp_scroll", { percent: m });
        }
      }
    }
    window.addEventListener(
      "scroll",
      function () {
        if (scrollTicking) return;
        scrollTicking = true;
        (window.requestAnimationFrame || setTimeout)(checkMilestones, 100);
      },
      { passive: true },
    );
  })();

  // Generic click capture. Single delegated handler in capture phase.
  // Skips: text inputs, anything under `[data-no-track]`, and clicks that
  // also trigger a specific event (ATC fetch / variant select / nav-away
  // anchors that emit elsewhere). Throttled to 1 event per 200ms.
  (function clickCapture() {
    var THROTTLE_MS = 200;
    var MAX_LABEL = 80;
    var lastFiredAt = 0;

    function closest(el, predicate) {
      while (el && el !== document) {
        if (predicate(el)) return el;
        el = el.parentElement;
      }
      return null;
    }

    function shortSelector(el) {
      var tag = (el.tagName || "").toLowerCase();
      if (el.id) return tag + "#" + el.id;
      var cls =
        typeof el.className === "string" && el.className.trim()
          ? el.className.trim().split(/\s+/)[0]
          : null;
      return cls ? tag + "." + cls : tag;
    }

    function clamp(s) {
      s = String(s || "").replace(/\s+/g, " ").trim();
      if (s.length > MAX_LABEL) s = s.slice(0, MAX_LABEL - 1) + "…";
      return s;
    }

    function spacedText(node) {
      // Walk children and insert spaces between distinct elements so a
      // markup like `<span>Cart</span><span>11</span> item` reads
      // "Cart 11 item" instead of "Cart11 item".
      if (!node) return "";
      var parts = [];
      var stack = [node];
      while (stack.length) {
        var n = stack.shift();
        if (!n) continue;
        if (n.nodeType === 3) {
          var t = String(n.nodeValue || "").trim();
          if (t) parts.push(t);
        } else if (n.nodeType === 1) {
          var children = n.childNodes;
          for (var i = 0; i < children.length; i++) stack.push(children[i]);
        }
      }
      return parts.join(" ");
    }

    function readLabel(el) {
      var host = closest(el, function (n) {
        var t = (n.tagName || "").toLowerCase();
        return t === "button" || t === "a";
      }) || el;
      // Priority: aria-label > title > data-label > spaced textContent >
      // alt (img) > visually-hidden text helper > selector hint.
      var candidates = [
        host.getAttribute && host.getAttribute("aria-label"),
        host.getAttribute && host.getAttribute("title"),
        host.getAttribute && host.getAttribute("data-label"),
        spacedText(host),
        el.tagName === "IMG" ? el.getAttribute("alt") : null,
      ];
      for (var i = 0; i < candidates.length; i++) {
        var v = clamp(candidates[i]);
        if (v) return v;
      }
      // Icon-only fallback. Try common class hints like `icon-cart`, `svg-cart`.
      if (host.querySelector) {
        var svg = host.querySelector("svg");
        if (svg) {
          var cls = (svg.getAttribute("class") || "")
            .split(/\s+/)
            .find(function (c) {
              return /^icon[-_]|cart|menu|close|search|user|heart|account|wishlist/i.test(c);
            });
          if (cls) return clamp(cls.replace(/^icon[-_]/i, "") + " icon");
        }
        if (host.querySelector("img,svg")) return "icon button";
      }
      return null;
    }

    function productContext(el) {
      // Capture nearest product-card / PDP context so timeline entries on
      // collection pages aren't just `tag=button, label=+ Add`.
      var ctx = {};
      var card = closest(el, function (n) {
        if (!n.classList) return false;
        return (
          n.classList.contains("card-wrapper") ||
          n.classList.contains("product-card") ||
          n.classList.contains("card") ||
          (n.hasAttribute && n.hasAttribute("data-product-handle"))
        );
      });
      if (!card) return null;
      var handle =
        (card.getAttribute && card.getAttribute("data-product-handle")) ||
        (card.querySelector && (function () {
          var a = card.querySelector('a[href*="/products/"]');
          if (!a) return null;
          var m = a.getAttribute("href").match(/\/products\/([^/?#]+)/);
          return m ? m[1] : null;
        })());
      if (handle) ctx.product_handle = String(handle).slice(0, 128);
      var pid = card.getAttribute && card.getAttribute("data-product-id");
      if (pid) ctx.product_id = String(pid).slice(0, 64);
      return Object.keys(ctx).length ? ctx : null;
    }

    document.addEventListener(
      "click",
      function (e) {
        try {
          var target = e.target;
          if (!target || target.nodeType !== 1) return;

          // Skip text inputs and form controls — avoids PII / value capture.
          var tag = (target.tagName || "").toLowerCase();
          if (tag === "input" || tag === "textarea" || tag === "select") return;
          if (closest(target, function (n) {
            var t = (n.tagName || "").toLowerCase();
            return t === "input" || t === "textarea" || t === "select";
          })) return;

          // Honour opt-out.
          if (closest(target, function (n) {
            return n.hasAttribute && n.hasAttribute("data-no-track");
          })) return;

          // Newsletter popup has dedicated events — skip generic click.
          if (closest(target, function (n) {
            return n.id === "DluxNewsletterPopup";
          })) return;

          // Predictive search modal has dedicated events — skip generic click.
          if (closest(target, function (n) {
            var tag = (n.tagName || "").toLowerCase();
            if (tag === "predictive-search") return true;
            if (n.classList && (
              n.classList.contains("search-modal") ||
              n.classList.contains("search-modal__form") ||
              n.classList.contains("dlux-search-modal-form")
            )) return true;
            return false;
          })) return;

          // Dedicated events handle these subtrees — skip generic click.
          if (closest(target, function (n) {
            if (!n.classList) return false;
            return (
              n.classList.contains("dlux-ai-chat__widget") ||
              n.classList.contains("dlux-ai-faq__item") ||
              n.classList.contains("faq-ask__item") ||
              n.classList.contains("quantity") ||
              n.classList.contains("cart__checkout-button") ||
              n.id === "CartDrawer-Checkout"
            );
          })) return;

          // Skip clicks that are already covered by specific handlers.
          // ATC button → submit / fetch path emits add_to_cart elsewhere.
          var atcAncestor = closest(target, function (n) {
            return (
              (n.getAttribute && (n.getAttribute("name") === "add" ||
                /add[-_ ]?to[-_ ]?cart/i.test(n.getAttribute("data-action") || "") ||
                /add[-_ ]?to[-_ ]?cart/i.test(n.id || ""))) ||
              false
            );
          });
          if (atcAncestor) return;

          // Email-when-back link → notify_intent hook emits its own event.
          var notifyAncestor = closest(target, function (n) {
            return n.classList && n.classList.contains("dlux-card-notify-link");
          });
          if (notifyAncestor) return;

          // Collection Load More button → collection_load_more event emitted in
          // dlux-collection-load-more.js with collection_handle + page context.
          var loadMoreAncestor = closest(target, function (n) {
            return n.hasAttribute && n.hasAttribute("data-dlux-load-more");
          });
          if (loadMoreAncestor) return;

          var now = Date.now();
          if (now - lastFiredAt < THROTTLE_MS) return;
          lastFiredAt = now;

          var data = { tag: tag };
          var role = target.getAttribute && target.getAttribute("role");
          if (role) data.role = role;

          var label = readLabel(target);
          if (label) data.label = label;

          // Anchor metadata.
          var anchor = closest(target, function (n) {
            return (n.tagName || "").toLowerCase() === "a";
          });
          if (anchor && anchor.href) {
            data.href = String(anchor.href).slice(0, 512);
            try {
              data.external =
                new URL(anchor.href, window.location.href).host !==
                window.location.host;
            } catch (_) {}
          }

          // Nearest data-product-id (matches the ATC convention).
          var pidEl = closest(target, function (n) {
            return n.hasAttribute && n.hasAttribute("data-product-id");
          });
          if (pidEl) {
            data.product_id = String(
              pidEl.getAttribute("data-product-id") || "",
            ).slice(0, 64);
          }

          // Product card context (collection grid quick-add, recommendation row, etc.)
          var pctx = productContext(target);
          if (pctx) {
            if (pctx.product_handle) data.product_handle = pctx.product_handle;
            if (pctx.product_id && !data.product_id) data.product_id = pctx.product_id;
          }

          data.selector = shortSelector(target);

          push("click", data);
        } catch (_) {}
      },
      true,
    );
  })();

  // Newsletter popup — dedicated events for open, submit, dismiss.
  // Watches #DluxNewsletterPopup for attribute changes (the theme toggles
  // `hidden` + `aria-hidden` to show/hide). Form submit and any [data-close]
  // click inside the popup get their own events. Generic click capture
  // skips this subtree (see click handler above) so no double-counting.
  (function newsletterPopupHook() {
    function attach(popup) {
      if (!popup || popup.__dluxJourneyHooked) return;
      popup.__dluxJourneyHooked = true;

      var lastVisible = isPopupVisible(popup);

      // Open detection.
      var observer = new MutationObserver(function () {
        var visible = isPopupVisible(popup);
        if (visible && !lastVisible) {
          push("popup_view", { popup: "newsletter" });
        }
        lastVisible = visible;
      });
      observer.observe(popup, {
        attributes: true,
        attributeFilter: ["hidden", "aria-hidden", "class"],
      });

      // Form submit.
      var form = popup.querySelector("form");
      if (form) {
        form.addEventListener(
          "submit",
          function () {
            var email = form.querySelector('input[type="email"]');
            var hasEmail = !!(email && email.value && email.value.indexOf("@") !== -1);
            push("popup_submit", {
              popup: "newsletter",
              has_email: hasEmail,
            });
          },
          true,
        );
      }

      // Dismiss clicks (close button, dismiss button, backdrop).
      popup.addEventListener(
        "click",
        function (e) {
          var t = e.target;
          if (!t || t.nodeType !== 1) return;
          var dismiss = t.closest && t.closest("[data-close]");
          if (!dismiss) return;
          var role = dismiss.classList.contains("dlux-newsletter-popup__close")
            ? "close_button"
            : dismiss.classList.contains("dlux-newsletter-popup__backdrop")
              ? "backdrop"
              : "dismiss_button";
          push("popup_dismiss", { popup: "newsletter", via: role });
        },
        true,
      );
    }

    function isPopupVisible(el) {
      if (!el) return false;
      if (el.hasAttribute("hidden")) return false;
      if (el.getAttribute("aria-hidden") === "true") return false;
      // Defensive: if CSS hides via display:none, treat as hidden too.
      var cs = window.getComputedStyle ? window.getComputedStyle(el) : null;
      if (cs && (cs.display === "none" || cs.visibility === "hidden")) return false;
      return true;
    }

    function tryAttach() {
      attach(document.getElementById("DluxNewsletterPopup"));
    }

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", tryAttach);
    } else {
      tryAttach();
    }

    // Popup may be injected after load — also watch the body briefly.
    var bodyObs = new MutationObserver(function () {
      var el = document.getElementById("DluxNewsletterPopup");
      if (el) {
        attach(el);
        bodyObs.disconnect();
      }
    });
    if (document.body) {
      bodyObs.observe(document.body, { childList: true, subtree: true });
      // Stop watching after 30s — popup either exists by then or never will.
      setTimeout(function () {
        bodyObs.disconnect();
      }, 30000);
    }
  })();

  // Predictive search modal — capture opens, queries, no-results, result clicks.
  // Modal markup: `<details>` wrapping `<predictive-search>` (see
  // snippets/header-search.liquid). Input: `.dlux-search-modal__input`.
  // Suggestion fetch hits Shopify's predictive_search_url (≈ /search/suggest).
  (function searchModalHook() {
    var QUERY_DEBOUNCE_MS = 300;
    var queryTimer = null;
    var lastEmittedQuery = "";
    var lastNoResultsQuery = "";

    function searchInput() {
      return document.querySelector(".dlux-search-modal__input");
    }

    function searchScope(target) {
      // Anything inside the modal markup we care about.
      while (target && target !== document) {
        var tag = (target.tagName || "").toLowerCase();
        if (tag === "predictive-search") return target;
        if (
          target.classList &&
          (target.classList.contains("search-modal") ||
            target.classList.contains("search-modal__form") ||
            target.classList.contains("dlux-search-modal-form"))
        )
          return target;
        target = target.parentElement;
      }
      return null;
    }

    // 1. Modal open — delegated `toggle` on document for the wrapping <details>.
    document.addEventListener(
      "toggle",
      function (e) {
        var el = e.target;
        if (!el || el.tagName !== "DETAILS") return;
        if (!el.open) return;
        // Only fire for the search details (contains <predictive-search>).
        if (!el.querySelector("predictive-search")) return;
        push("search_modal_open");
      },
      true,
    );

    // 2. Query — debounced input listener on the search field. Attach lazily
    //    because the input may not exist at boot (rendered inside <details>).
    document.addEventListener(
      "input",
      function (e) {
        var t = e.target;
        if (!t || !t.classList || !t.classList.contains("dlux-search-modal__input")) return;
        var raw = String(t.value || "").trim();
        if (queryTimer) {
          clearTimeout(queryTimer);
          queryTimer = null;
        }
        if (!raw) return;
        var q = raw.slice(0, 256);
        queryTimer = setTimeout(function () {
          if (q === lastEmittedQuery) return;
          lastEmittedQuery = q;
          push("search_query", { q: q, length: q.length });
        }, QUERY_DEBOUNCE_MS);
      },
      true,
    );

    // 3. No-results — patch fetch to inspect predictive-search responses.
    if (window.fetch) {
      var origFetch = window.fetch;
      window.fetch = function (input, init) {
        var url = "";
        try {
          url = typeof input === "string" ? input : input && input.url;
        } catch (_) {}
        var isSuggest = url && /\/search\/suggest/.test(url);
        var pending = origFetch.apply(this, arguments);
        if (!isSuggest) return pending;
        try {
          pending
            .then(function (resp) {
              if (!resp || !resp.ok) return;
              var clone = resp.clone();
              var ct = clone.headers.get("content-type") || "";
              if (ct.indexOf("application/json") !== -1) {
                clone
                  .json()
                  .then(function (json) {
                    var results = json && json.resources && json.resources.results;
                    if (!results) return;
                    var total = 0;
                    Object.keys(results).forEach(function (k) {
                      var v = results[k];
                      if (Array.isArray(v)) total += v.length;
                    });
                    if (total !== 0) return;
                    var qParam = "";
                    try {
                      qParam = new URL(url, window.location.href).searchParams.get("q") || "";
                    } catch (_) {}
                    qParam = qParam.slice(0, 256);
                    if (!qParam || qParam === lastNoResultsQuery) return;
                    lastNoResultsQuery = qParam;
                    push("search_no_results", { q: qParam });
                  })
                  .catch(function () {});
              }
            })
            .catch(function () {});
        } catch (_) {}
        return pending;
      };
    }

    // 4. Result click — anchor inside search scope pointing at a known kind.
    document.addEventListener(
      "click",
      function (e) {
        var scope = searchScope(e.target);
        if (!scope) return;
        var anchor = e.target && e.target.closest ? e.target.closest("a") : null;
        if (!anchor || !anchor.href) return;
        var path = "";
        try {
          path = new URL(anchor.href, window.location.href).pathname;
        } catch (_) {
          return;
        }
        var kind = null;
        if (/^\/products\//.test(path)) kind = "product";
        else if (/^\/collections\//.test(path)) kind = "collection";
        else if (/^\/pages\//.test(path)) kind = "page";
        else if (/^\/blogs\//.test(path)) kind = "blog";
        if (!kind) return;
        var input = searchInput();
        var q = input ? String(input.value || "").trim().slice(0, 256) : "";
        push("search_result_click", {
          q: q,
          href: String(anchor.href).slice(0, 512),
          kind: kind,
        });
      },
      true,
    );
  })();

  // AI FAQ chat widget — opens, question submits, response received.
  // Markup: sections/dlux-ai-chat.liquid (`.dlux-ai-chat__widget`).
  // Endpoint: /api/ai/chat (admin.dlux-essentials.com).
  (function aiChatHook() {
    var MAX_Q = 256;
    var openedThisPage = false;
    var inflight = Object.create(null); // requestId → { sentAt, question }
    var requestSeq = 0;

    function widgetMeta(node) {
      var w = node && node.closest ? node.closest(".dlux-ai-chat__widget") : null;
      if (!w) return {};
      return {
        product_id: w.getAttribute("data-product-id") || undefined,
      };
    }

    // Open detection: first focus on the chat input per pageload.
    document.addEventListener(
      "focusin",
      function (e) {
        var t = e.target;
        if (!t || !t.matches) return;
        if (!t.matches("[data-dlux-ai-chat-input]")) return;
        if (openedThisPage) return;
        openedThisPage = true;
        var meta = widgetMeta(t);
        push("ai_chat_open", meta);
      },
      true,
    );

    // Submit detection: form submit, capture question text.
    document.addEventListener(
      "submit",
      function (e) {
        var form = e.target;
        if (!form || !form.matches || !form.matches("[data-dlux-ai-chat-form]")) return;
        var input = form.querySelector("[data-dlux-ai-chat-input]");
        var q = input ? String(input.value || "").trim() : "";
        if (!q) return;
        var rid = ++requestSeq;
        inflight[rid] = { sentAt: Date.now(), question: q };
        var data = widgetMeta(form);
        data.question = q.slice(0, MAX_Q);
        data.length = q.length;
        data._rid = rid;
        push("ai_chat_submit", data);
        // GC inflight if response never arrives (60s).
        setTimeout(function () {
          delete inflight[rid];
        }, 60000);
      },
      true,
    );

    // Response detection: patch fetch for /api/ai/chat. Latency + status.
    if (window.fetch) {
      var origFetch = window.fetch;
      window.fetch = function (input, init) {
        var url = "";
        try {
          url = typeof input === "string" ? input : input && input.url;
        } catch (_) {}
        var isChat = url && /\/api\/ai\/chat(\b|\/|$)/.test(url);
        var startedAt = isChat ? Date.now() : 0;
        var pending = origFetch.apply(this, arguments);
        if (!isChat) return pending;
        pending
          .then(function (resp) {
            try {
              push("ai_chat_response", {
                ms: Date.now() - startedAt,
                status: resp ? resp.status : 0,
                ok: !!(resp && resp.ok),
              });
            } catch (_) {}
          })
          .catch(function () {
            try {
              push("ai_chat_response", {
                ms: Date.now() - startedAt,
                status: 0,
                ok: false,
              });
            } catch (_) {}
          });
        return pending;
      };
    }
  })();

  // FAQ accordion expand — both dlux-ai-faq (uses <details>) and faq-ask
  // (uses [data-faq-ask-toggle] button). Captures which question opened.
  (function faqAccordionHook() {
    function readQuestionText(node) {
      if (!node) return "";
      var text = node.textContent || "";
      return String(text).replace(/\s+/g, " ").trim().slice(0, 200);
    }

    // dlux-ai-faq: <details class="dlux-ai-faq__item">
    document.addEventListener(
      "toggle",
      function (e) {
        var el = e.target;
        if (!el || el.tagName !== "DETAILS") return;
        if (!el.classList || !el.classList.contains("dlux-ai-faq__item")) return;
        if (!el.open) return;
        var summary = el.querySelector(".dlux-ai-faq__question");
        push("faq_open", {
          source: "ai_faq",
          question: readQuestionText(summary),
        });
      },
      true,
    );

    // faq-ask: <button data-faq-ask-toggle> with aria-expanded
    document.addEventListener(
      "click",
      function (e) {
        var btn = e.target && e.target.closest ? e.target.closest("[data-faq-ask-toggle]") : null;
        if (!btn) return;
        // Fire on transition to opened. After click, aria-expanded is still
        // the old value; the theme JS flips it. Defer one tick.
        setTimeout(function () {
          if (btn.getAttribute("aria-expanded") !== "true") return;
          var qText = btn.querySelector(".faq-ask__q-text");
          push("faq_open", {
            source: "faq_ask",
            question: readQuestionText(qText || btn),
          });
        }, 0);
      },
      true,
    );
  })();

  // Email-when-back link click — fired on collection cards (sold-out variants)
  // and Recently Viewed (sold-out cards). Both render an <a> with class
  // .dlux-card-notify-link pointing at the PDP's #dlux-restock-form anchor.
  // Dedicated event lets us measure the OOS-recovery funnel:
  //   notify_intent (this) → notify_view (form renders on PDP) → notify_submit
  (function notifyIntentHook() {
    document.addEventListener(
      "click",
      function (e) {
        try {
          var link =
            e.target && e.target.closest
              ? e.target.closest(".dlux-card-notify-link")
              : null;
          if (!link) return;

          // Surface detection: Recently Viewed strip vs main collection grid.
          var inRecentlyViewed = !!link.closest(
            ".dlux-recently-viewed, [class*='recently-viewed']",
          );
          var surface = inRecentlyViewed ? "recently_viewed" : "collection_card";

          // Product context (handle/id) from the nearest card wrapper.
          var data = { surface: surface };
          var href = link.getAttribute("href") || "";
          if (href) data.href = href.slice(0, 512);
          var handleMatch = href.match(/\/products\/([^/?#]+)/);
          if (handleMatch) data.product_handle = handleMatch[1];
          var pidEl =
            link.closest && link.closest("[data-product-id]");
          if (pidEl) {
            data.product_id = String(
              pidEl.getAttribute("data-product-id") || "",
            ).slice(0, 64);
          }
          push("notify_intent", data);
        } catch (_) {}
      },
      true,
    );
  })();

  // Cart-drawer checkout button — fire begin_checkout immediately on click.
  // The existing URL-based detection only fires once the browser lands on
  // /checkouts/..., which is a different ingest call (and sometimes lost to
  // navigation timing). Capturing the click gives us the intent + cart size
  // before redirect.
  (function checkoutButtonHook() {
    document.addEventListener(
      "click",
      function (e) {
        try {
          var btn =
            e.target && e.target.closest
              ? e.target.closest(
                  '#CartDrawer-Checkout, .cart__checkout-button, button[name="checkout"]',
                )
              : null;
          if (!btn) return;
          if (btn.disabled) return;
          var data = { source: "cart_drawer" };
          // Cart line count via DOM hint.
          var qtyEls = document.querySelectorAll(
            "#CartDrawer .cart-item, .cart-items .cart-item",
          );
          if (qtyEls && qtyEls.length) data.line_count = qtyEls.length;
          push("begin_checkout", data);
          // Flush right away so the event survives the navigation.
          flush(true);
        } catch (_) {}
      },
      true,
    );
  })();

  // Email capture — catch-all <form> submit listener. Extracts the first
  // valid email input value, stores it in localStorage so subsequent batches
  // re-send via meta.email, and pushes a one-shot `identify` event so the
  // moment of identification appears in the timeline. Server hashes + masks
  // the email; plaintext never lands in events.
  (function emailCaptureHook() {
    function findEmail(form) {
      var inputs = form.querySelectorAll(
        'input[type="email"], input[name*="email" i], input[name="contact[email]"]',
      );
      for (var i = 0; i < inputs.length; i++) {
        var v = String(inputs[i].value || "").trim().toLowerCase();
        if (v && EMAIL_RE.test(v)) return v;
      }
      return null;
    }

    function formLabel(form) {
      if (form.id) return form.id;
      if (form.getAttribute("data-dlux-ai-chat-form") !== null) return "ai_chat";
      var cls = typeof form.className === "string" ? form.className.trim() : "";
      if (cls) return cls.split(/\s+/)[0];
      var action = form.getAttribute("action") || "";
      if (action) return action.split("?")[0];
      return "form";
    }

    document.addEventListener(
      "submit",
      function (e) {
        try {
          var form = e.target;
          if (!form || form.tagName !== "FORM") return;
          // Honour opt-out.
          var n = form;
          while (n && n !== document) {
            if (n.hasAttribute && n.hasAttribute("data-no-track")) return;
            n = n.parentElement;
          }
          var email = findEmail(form);
          if (!email) return;
          // Dedupe — same email already captured, just keep sending via meta.
          if (pendingEmail === email) return;
          pendingEmail = email;
          writeStoredEmail(email);
          push("identify", {
            source: String(formLabel(form)).slice(0, 64),
            masked: maskEmail(email),
          });
        } catch (_) {}
      },
      true,
    );
  })();

  // Purchase event — Shopify thank-you page (/orders/<token> or /checkouts/.../thank_you).
  (function purchaseHook() {
    var p = window.location.pathname;
    if (/\/thank_you|\/orders\//.test(p)) {
      var orderId = null;
      try {
        if (
          window.Shopify &&
          window.Shopify.checkout &&
          window.Shopify.checkout.order_id
        ) {
          orderId = String(window.Shopify.checkout.order_id);
        }
      } catch (_) {}
      push("purchase", orderId ? { order_id: orderId } : undefined);
    }
  })();

  // Cart-drawer open/close + cart_view. Observe <cart-drawer> for `active`
  // class flips (Dawn pattern). On open: fire cart_drawer_open + cart_view.
  // On close: fire cart_drawer_close with dwell_ms.
  (function cartDrawerVisibilityHook() {
    function readLineCount() {
      var els = document.querySelectorAll(
        "#CartDrawer .cart-item, #CartDrawer cart-drawer-items .cart-item, .cart-items .cart-item",
      );
      return els && els.length ? els.length : 0;
    }
    function readSubtotalCents() {
      // Best-effort scrape of Dawn's subtotal node. Returns null if unreadable.
      var el = document.querySelector(
        "#CartDrawer .totals__subtotal-value, #CartDrawer .totals__total, .cart-drawer .totals__subtotal-value",
      );
      if (!el) return null;
      var t = el.textContent || "";
      var m = t.replace(/[^0-9.,]/g, "").replace(/,/g, "");
      var n = parseFloat(m);
      return isFinite(n) ? Math.round(n * 100) : null;
    }
    function attach(drawer) {
      if (!drawer || drawer.__dluxCartVisHook) return;
      drawer.__dluxCartVisHook = true;
      var openAt = 0;
      var wasActive = drawer.classList.contains("active");
      if (wasActive) {
        openAt = Date.now();
        push("cart_drawer_open");
        // Defer cart_view a frame so DOM populates after add-to-cart refresh.
        requestAnimationFrame(function () {
          var data = { line_count: readLineCount() };
          var c = readSubtotalCents();
          if (c != null) data.value_cents = c;
          push("cart_view", data);
        });
      }
      var obs = new MutationObserver(function () {
        var active = drawer.classList.contains("active");
        if (active && !wasActive) {
          openAt = Date.now();
          push("cart_drawer_open");
          requestAnimationFrame(function () {
            var data = { line_count: readLineCount() };
            var c = readSubtotalCents();
            if (c != null) data.value_cents = c;
            push("cart_view", data);
          });
        } else if (!active && wasActive) {
          var dwell = openAt ? Date.now() - openAt : 0;
          push("cart_drawer_close", { dwell_ms: dwell });
        }
        wasActive = active;
      });
      obs.observe(drawer, { attributes: true, attributeFilter: ["class"] });
    }
    var existing = document.querySelector("cart-drawer");
    if (existing) attach(existing);
    // Re-arm if Shopify swaps the drawer node.
    var bodyObs = new MutationObserver(function () {
      var d = document.querySelector("cart-drawer");
      if (d) attach(d);
    });
    if (document.body) {
      bodyObs.observe(document.body, { childList: true, subtree: true });
    }
  })();

  // Cart line-item mutation hook. Patches fetch to catch POSTs to
  // /cart/change.js and /cart/update.js. Quantity 0 = remove_from_cart;
  // non-zero with a target line/id = quantity_change.
  (function patchCartMutations() {
    if (!window.fetch) return;
    var origFetch = window.fetch;
    function parseBody(init) {
      if (!init || !init.body) return null;
      var body = init.body;
      try {
        if (typeof body === "string") {
          if (body[0] === "{") return JSON.parse(body);
          var p = new URLSearchParams(body);
          var o = {};
          p.forEach(function (v, k) {
            o[k] = v;
          });
          return o;
        }
        if (body instanceof FormData) {
          var o2 = {};
          body.forEach(function (v, k) {
            o2[k] = v;
          });
          return o2;
        }
      } catch (_) {}
      return null;
    }
    window.fetch = function (input, init) {
      try {
        var url = typeof input === "string" ? input : input && input.url;
        if (url && /\/cart\/(change|update)(\.js)?/.test(url)) {
          var parsed = parseBody(init) || {};
          // /cart/change.js shape: { id: "<key>"|number, quantity: N, line?: N }
          // /cart/update.js shape: { updates: { "<variant_id>": N, ... } }
          if (parsed.updates && typeof parsed.updates === "object") {
            for (var k in parsed.updates) {
              if (!Object.prototype.hasOwnProperty.call(parsed.updates, k)) continue;
              var q = Number(parsed.updates[k]);
              if (q === 0) {
                push("remove_from_cart", { variant_id: String(k), source: "cart_update" });
              } else if (!isNaN(q)) {
                push("quantity_change", {
                  variant_id: String(k),
                  quantity: q,
                  source: "cart_update",
                });
              }
            }
          } else if (parsed.id != null || parsed.line != null) {
            var qty = Number(parsed.quantity);
            var idVal = parsed.id != null ? String(parsed.id) : null;
            var data = { source: "cart_change" };
            if (idVal) data.line_key = idVal;
            if (parsed.line != null) data.line = Number(parsed.line);
            if (qty === 0) {
              push("remove_from_cart", data);
            } else if (!isNaN(qty)) {
              data.quantity = qty;
              push("quantity_change", data);
            }
          }
        }
      } catch (_) {}
      return origFetch.apply(this, arguments);
    };
  })();

  // ── Boot ───────────────────────────────────────────────────────────────
  emitPageView();
  emitContextEvent();
  startHeartbeat();
  scheduleFlush();

  // Expose a tiny manual hook in case the theme wants to fire custom clicks.
  window.dluxJourney = {
    track: function (type, data, path) {
      push(type, data, path);
    },
  };
})();
